import { Close } from '@mui/icons-material';
import { Box, Typography, Divider, IconButton } from '@mui/material';

import { IHydrantProperties } from '@/models';

interface FireHydrantPopupProps {
  data: IHydrantProperties;
  onClose?: () => void;
}

const FireHydrantPopup = ({ data, onClose }: FireHydrantPopupProps) => {
  // Extract values from the data
  const location = data.location || '';
  const street = data.street || '';
  const sideOfStreet = data.sideOfStreet || '';
  const tapeColor = data.tapeColor || '';
  const hydrantNo = data.hydrantNumber || '';
  const extension = data.extension || '';
  const fid = data.hydrantFID ? data.hydrantFID.split('.')[0] : '';
  const fireDepartment = data.fireDepartment || '';
  const owner = data.owner || '';
  const latitude = data.latitude || '';
  const longitude = data.longitude || '';

  const labelStyle = {
    color: 'text.secondary',
    fontSize: '0.6rem',
    lineHeight: 1,
    mb: 0,
  };

  const valueStyle = {
    fontWeight: 'medium',
    fontSize: '0.7rem',
    lineHeight: 1,
  };

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        borderRadius: 1,
        fontSize: '0.65rem',
        py: 0.5,
        minWidth: 250,
      }}
    >
      {/* Location section (with close button) */}
      <Box sx={{ px: 0.4, pt: 0.4, position: 'relative' }}>
        <Typography variant="inherit" sx={labelStyle}>
          Location
        </Typography>
        <Typography variant="inherit" sx={valueStyle}>
          {location}
        </Typography>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              padding: 0.2,
              color: 'text.secondary',
            }}
            size="small"
          >
            <Close sx={{ fontSize: '0.8rem' }} />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ my: 0.2 }} />

      {/* Street, Side, Color row */}
      <Box sx={{ display: 'flex', px: 0.4, pt: 0.2 }}>
        <Box sx={{ flex: 1, pr: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Street
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {street}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, px: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Side of street
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {sideOfStreet}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, pl: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Tape color
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {tapeColor}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 0.2 }} />

      {/* Hydrant, Extension, FID row */}
      <Box sx={{ display: 'flex', px: 0.4, pt: 0.2 }}>
        <Box sx={{ flex: 1, pr: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Hydrant no
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {hydrantNo}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, px: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Extension
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {extension}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, pl: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            FID
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {fid}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 0.2 }} />

      {/* Fire department section */}
      <Box sx={{ px: 0.4, pt: 0.2 }}>
        <Typography variant="inherit" sx={labelStyle}>
          Fire department
        </Typography>
        <Typography variant="inherit" sx={valueStyle}>
          {fireDepartment}
        </Typography>
      </Box>

      <Divider sx={{ my: 0.2 }} />

      {/* Owner, Latitude, Longitude row */}
      <Box sx={{ display: 'flex', px: 0.4, pt: 0.2 }}>
        <Box sx={{ flex: 1, pr: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Owner
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {owner}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, px: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Latitude
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {latitude}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, pl: 0.2 }}>
          <Typography variant="inherit" sx={labelStyle}>
            Longitude
          </Typography>
          <Typography variant="inherit" sx={valueStyle}>
            {longitude}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FireHydrantPopup;
